import React, { useEffect, useState } from 'react';
import { User } from "../typings";
import { FaAngleLeft, FaAngleRight, FaSort, FaSortDown, FaSortUp } from "react-icons/fa6";
import { Align, Row } from "../components/Flex";
import TextField from "../components/TextField";
import { addUser, createUser, fetchOrgInfo, fetchUsers, removeUser } from "./users-api";
import UserRow from "./UserRow";
import { PrimaryButton } from "../components/buttons/Buttons";
import AddUserDialog from "./AddUserDialog";
import CreateUserDialog from "./CreateUserDialog";
import { useAuth } from "../auth-context";
import { useApi } from "../api-context";
import { toast } from "react-toastify";

function UsersPage() {
    const api = useApi();
    const { data } = useAuth();
    const [total, setTotal] = useState(0);
    const [users, setUsers] = useState<User[]>([]);

    const [page, setPage] = useState(0);
    const [emailFilter, setEmailFilter] = useState('');
    const [orderBy, setOrderBy] = useState('id');
    const [orderDirection, setOrderDirection] = useState('ASC');

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [max, setMax] = useState<number>();
    const [remaining, setRemaining] = useState<number>();

    const loadUsers = (page: number, orderBy: string, orderDirection: string, emailFilter?: string) => {
        fetchUsers(api, { orderBy, orderDirection, page, emailFilter })
            .then(res => {
                setTotal(res.total);
                setUsers(res.users);
            })
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load users');
                }
            });
    };

    const onDelete = (id: number) => {
        removeUser(api, id)
            .then(() => {
                loadUsers(page, orderBy, orderDirection, emailFilter || undefined);
                toast.success('Removed user')
            })
            .catch(() => toast.error('Failed to remove user'));
    };

    const onAdd = () => {
        fetchOrgInfo(api)
            .then(data => {
                setAddModalOpen(true);
                setMax(data.maxRemainingSlotsCount);
            });
    };

    const onCreate = () => {
        fetchOrgInfo(api)
            .then(data => {
                setCreateModalOpen(true);
                setMax(data.maxRemainingSlotsCount);
                setRemaining(data.remainingSlotsCount)
            });
    };

    const onAddUser = (email: string) => {
        addUser(api, email)
            .then(() => {
                loadUsers(page, orderBy, orderDirection, emailFilter || undefined);
                toast.success('Added user');
            })
            .catch((reason) => {
                toast.error('Failed to add user: ' + reason);
            })
            .finally(() => setAddModalOpen(false));
    };

    const onCreateUser = (type: string, email: string) => {
        createUser(api, type, email)
            .then(() => {
                loadUsers(page, orderBy, orderDirection, emailFilter || undefined);
                toast.success('Created user');
            })
            .catch((reason) => {
                toast.error('Failed to create user: ' + reason);
            })
            .finally(() => setCreateModalOpen(false));
    };

    const onFilter = (nameFilter: string) => {
        setPage(0);
        setEmailFilter(nameFilter);
    }

    const onChangeOrder = (newOrderBy: string) => {
        setPage(0);
        if (newOrderBy === orderBy) {
            setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
            return;
        }
        setOrderBy(newOrderBy);
        setOrderDirection('ASC')
    }

    useEffect(() => {
        loadUsers(page, orderBy, orderDirection, emailFilter || undefined);
    }, [orderBy, orderDirection, emailFilter, page]);

    const renderColumnHeader = (title: string, prop?: string, flex?: number) => {
        return (
            <div style={{ flex, display: 'flex' }}>
                <div className="column-header" onClick={prop ? () => onChangeOrder(prop) : undefined}>
                    <div>{title}</div>
                    {
                        prop ? (
                            orderBy !== prop
                                ? <FaSort/>
                                : orderDirection === 'ASC' ? <FaSortUp/> : <FaSortDown/>
                        ) : null
                    }
                </div>
            </div>
        );
    }

    if (data?.memberType !== 'ADMIN') {
        return null;
    }

    return (
        <>
            <div className="page-container drill-files-page">
                <Row gap={8} style={{marginBottom: '32px'}} align={Align.SPACE_BETWEEN} >
                    <div style={{ flex: 1 }} className="main-title">
                        Users
                    </div>
                    <Row gap={8}>
                        <PrimaryButton label="Add existing" onClick={onAdd} />
                        {
                            data.organisationType !== 'ALLIANCE'
                                ? <PrimaryButton label="Create new" onClick={onCreate} />
                                : <></>
                        }
                    </Row>
                </Row>
                <Row align={Align.SPACE_BETWEEN} className="search-header">
                    <TextField label="" value={emailFilter} setValue={onFilter} placeholder="Filter by name"/>
                    <div style={{ color: '#545452' }}>
                        {`Showing ${page * 10 + (total ? 1 : 0)}-${Math.min(total, (page + 1) * 10)} of ${total}`}
                    </div>
                </Row>
                <Row style={{ padding: '12px 16px' }}>
                    {renderColumnHeader('Email', 'email', 2)}
                    {renderColumnHeader('Account', undefined, 1)}
                    {renderColumnHeader('License', undefined, 1)}
                    {renderColumnHeader('Portal user right', undefined, 2)}
                    <div style={{ width: '28px' }}/>
                </Row>
                <div className="content-container" style={{ marginBottom: '32px', gap: '4px' }}>
                    {users.map((user) => (
                        <UserRow
                            key={user.id}
                            user={user}
                            onRemove={onDelete}
                        />
                    ))}
                </div>
                <Row align={Align.END} gap={4}>
                    <FaAngleLeft
                        className="pager-icon"
                        onClick={() => setPage(Math.max(0, page - 1))}
                        size={20}
                        color="#545452"
                    />
                    <div style={{
                        fontSize: '16px',
                        fontFamily: 'InterRegular',
                        color: '#545452'
                    }}>
                        {page + 1} of {Math.ceil(total / 10)}
                    </div>
                    <FaAngleRight
                        className="pager-icon"
                        size={20}
                        onClick={() => setPage(Math.min(Math.floor(total / 10), page + 1))}
                        color="#545452"
                    />
                </Row>
            </div>
            <AddUserDialog
                max={max || 0}
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onAdd={onAddUser}
            />
            <CreateUserDialog
                max={max || 0}
                remaining={remaining || 0}
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onAdd={onCreateUser}
            />
        </>
    );
}
export default UsersPage;
